'use client';

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useRouter, usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';
import { fetchInboxUnreadCount, subscribeToMessages, unsubscribeFromMessages } from '@/lib/messages';

interface SidebarProps {
  userName?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ userName = 'John Doe' }) => {
  const router = useRouter();
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Track the current path inside realtime callbacks without re-subscribing.
  const pathnameRef = useRef(pathname);
  useEffect(() => { pathnameRef.current = pathname; }, [pathname]);

  // Unread support-message badge: count on mount, keep live, and clear when
  // the client opens their messages page.
  useEffect(() => {
    let channel: ReturnType<typeof subscribeToMessages> | null = null;
    let active = true;

    const setup = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !active) return;

      const refresh = async () => {
        const { count } = await fetchInboxUnreadCount(user.id);
        if (active) setUnreadMessages(pathnameRef.current === '/client-dashboard/messages' ? 0 : count);
      };
      await refresh();

      channel = subscribeToMessages(user.id, (msg) => {
        // New support reply and the client isn't already on the messages page.
        if (msg.sender_role === 'admin' && pathnameRef.current !== '/client-dashboard/messages') {
          setUnreadMessages((prev) => prev + 1);
        }
      });

      const onRead = () => setUnreadMessages(0);
      const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
      window.addEventListener('client-messages-read', onRead);
      document.addEventListener('visibilitychange', onVisible);

      cleanupExtras = () => {
        window.removeEventListener('client-messages-read', onRead);
        document.removeEventListener('visibilitychange', onVisible);
      };
    };

    let cleanupExtras: (() => void) | null = null;
    setup();

    return () => {
      active = false;
      if (channel) unsubscribeFromMessages(channel);
      if (cleanupExtras) cleanupExtras();
    };
  }, []);

  // Clear the badge whenever the client navigates to the messages page.
  useEffect(() => {
    if (pathname === '/client-dashboard/messages') setUnreadMessages(0);
  }, [pathname]);

  // Toggle the mobile drawer from the top-bar hamburger (decoupled via a window event)
  useEffect(() => {
    const handler = () => setMobileOpen((o) => !o);
    window.addEventListener('toggle-sidebar', handler);
    return () => window.removeEventListener('toggle-sidebar', handler);
  }, []);

  // Close the drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const menuItems = [
    { icon: 'Squares2X2Icon', label: 'Dashboard', path: '/client-dashboard' },
    { icon: 'HomeIcon', label: 'Home', path: '/' },
    { icon: 'PlusCircleIcon', label: 'Place Order', path: '/image-upload-and-order-creation' },
    { icon: 'ChatBubbleLeftRightIcon', label: 'Messages', path: '/client-dashboard/messages' },
    { icon: 'CreditCardIcon', label: 'Billing', path: '/client-dashboard/billing' },
    { icon: 'UserIcon', label: 'Profile', path: '/client-dashboard/profile' },
    { icon: 'QuestionMarkCircleIcon', label: 'Support', path: '/contact' },
  ];

  const isActive = (path: string) => pathname === path;

  const go = (path: string) => {
    setMobileOpen(false);
    router.push(path);
  };

  const NavInner = () => (
    <>
      {/* Logo */}
      <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between">
        <button onClick={() => go('/homepage')} className="flex items-center group">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/shashio-logo.svg" alt="Shashio" className="h-7 w-auto transition-transform duration-300 group-hover:scale-105" />
        </button>
        {/* Close button (mobile only) */}
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
          aria-label="Close menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <div className="space-y-1">
          {menuItems.map((item) => {
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => go(item.path)}
                className={`group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 text-left ${
                  active
                    ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-lg shadow-purple-500/30'
                    : 'text-gray-400 hover:bg-white/[0.06] hover:text-white'
                }`}
              >
                {active && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-white/80" />
                )}
                <Icon
                  name={item.icon as any}
                  size={20}
                  variant="outline"
                  style={{ color: active ? '#FFFFFF' : 'currentColor' }}
                />
                <span className="font-medium text-sm">{item.label}</span>
                {item.label === 'Messages' && unreadMessages > 0 && (
                  <span
                    className={`ml-auto min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-[11px] font-bold text-white bg-red-500 ${active ? 'ring-2 ring-white/30' : 'shadow-lg shadow-red-500/40'} animate-pulse`}
                    title={`${unreadMessages} unread ${unreadMessages === 1 ? 'message' : 'messages'}`}
                  >
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Upgrade / help card */}
      <div className="p-3">
        <div className="relative overflow-hidden rounded-2xl border border-purple-500/20 bg-gradient-to-br from-purple-600/20 to-blue-600/10 p-4">
          <div className="absolute -top-8 -right-8 w-24 h-24 bg-purple-500/20 rounded-full blur-2xl" />
          <div className="relative">
            <p className="text-sm font-semibold text-white mb-1">Need more edits?</p>
            <p className="text-xs text-gray-400 mb-3">Start a new order in seconds.</p>
            <button
              onClick={() => go('/image-upload-and-order-creation')}
              className="w-full py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white transition-all"
            >
              Place Order
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar (in-flow) */}
      <div
        className="hidden lg:flex w-64 h-screen flex-col border-r backdrop-blur-sm"
        style={{
          backgroundColor: 'rgba(15, 16, 20, 0.85)',
          borderColor: 'rgba(31, 41, 55, 0.5)',
        }}
      >
        <NavInner />
      </div>

      {/* Mobile drawer — portaled to <body> so it escapes any parent stacking context */}
      {mounted && mobileOpen && createPortal(
        <div className="lg:hidden fixed inset-0 z-[9999]">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="absolute left-0 top-0 bottom-0 w-72 max-w-[80vw] flex flex-col border-r shadow-2xl animate-sidebar-in"
            style={{
              backgroundColor: '#0B0D17',
              borderColor: 'rgba(31, 41, 55, 0.5)',
            }}
          >
            <NavInner />
          </div>
        </div>,
        document.body
      )}

      <style jsx global>{`
        @keyframes sidebar-in {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-sidebar-in { animation: sidebar-in 0.25s ease-out; }
      `}</style>
    </>
  );
};

export default Sidebar;
